/**
 * CP-14.3 — Crew Shape Search (capability + capacity matcher).
 *
 * Capability-first, capacity-first discovery. The client describes the crew
 * they need (multiple resource types × qty × site-lead requirements), an
 * optional Phase-12 track, region, and verification toggle, and the matcher
 * returns owners (companies + solo contractors) whose `workforce_offering`
 * rows satisfy the shape.
 *
 * Lock §51 honored: no staff-count multiplier; cards do NOT show "size" as a
 * ranking signal — only as an informational employee_count_band pill.
 * Keyword search is intentionally a secondary, de-emphasized control.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import SendRFQModal from "@/components/SendRFQModal";

function Pill({ tone = "neutral", children, testid }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-sky-50 text-sky-700 border-sky-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    company: "bg-indigo-50 text-indigo-700 border-indigo-200",
    self: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
}

const EVIDENCE_PILL = {
  company_verified: { tone: "company", label: "Company-verified" },
  client_verified: { tone: "info", label: "Client-verified" },
  self: { tone: "self", label: "Self-declared" },
};

function emptyReq() {
  return { title_code: "", quantity: 1, site_lead_capable: false };
}

export default function CrewShapeSearch({ marketplace = null, headerTitle = null, headerDescription = null }) {
  const [titles, setTitles] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [regions, setRegions] = useState([]);

  const [requirements, setRequirements] = useState([
    { title_code: "lead_tech", quantity: 1, site_lead_capable: true },
    { title_code: "level_4_technician", quantity: 2, site_lead_capable: false },
    { title_code: "helper_apprentice", quantity: 3, site_lead_capable: false },
  ]);
  const [trackSlug, setTrackSlug] = useState("");
  const [regionCode, setRegionCode] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [includeSolo, setIncludeSolo] = useState(true);
  const [includeCompanies, setIncludeCompanies] = useState(true);
  const [keyword, setKeyword] = useState("");

  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  // CP-14.6.3 — Send-RFQ modal state. We pin the requirements/track/region
  // at modal-open time so the user editing the search form afterward never
  // changes what's about to be sent.
  const [rfqTarget, setRfqTarget] = useState(null);
  const [rfqSnap, setRfqSnap] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [tax, tk, rg] = await Promise.all([
          api("/title-taxonomy"),
          api("/catalogs/tracks").catch(() => ({ items: [] })),
          api("/catalog/regions").catch(() => ({ items: [] })),
        ]);
        setTitles(tax.items || []);
        setTracks(tk.items || []);
        setRegions(rg.items || []);
      } catch (e) {
        toast.error(`Failed to load catalogs: ${e.message}`);
      }
    })();
  }, []);

  const usedCodes = useMemo(
    () => new Set(requirements.map((r) => r.title_code).filter(Boolean)),
    [requirements],
  );

  const updateReq = (idx, patch) =>
    setRequirements((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addReq = () => setRequirements((p) => [...p, emptyReq()]);
  const removeReq = (idx) =>
    setRequirements((p) => (p.length > 1 ? p.filter((_, i) => i !== idx) : p));

  const submit = async () => {
    const reqs = requirements.filter((r) => r.title_code && Number(r.quantity) > 0);
    if (reqs.length === 0) {
      toast.error("Add at least one crew requirement with a resource type.");
      return;
    }
    if (new Set(reqs.map((r) => r.title_code)).size !== reqs.length) {
      toast.error("Each requirement row must use a unique resource type.");
      return;
    }
    setBusy(true);
    try {
      const r = await api("/marketplace/crew-shape-search", {
        method: "POST",
        body: {
          requirements: reqs.map((r) => ({
            title_code: r.title_code,
            quantity: Number(r.quantity) || 1,
            site_lead_capable: !!r.site_lead_capable,
          })),
          track_slug: trackSlug || null,
          region_code: regionCode || null,
          verified_only: !!verifiedOnly,
          include_solo: !!includeSolo,
          include_companies: !!includeCompanies,
          keyword: keyword.trim() || null,
          marketplace: marketplace || null,
          limit: 25,
        },
      });
      setResults(r.items || []);
      setSearched(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6" data-testid={TID.cssRoot}>
        <header className="mb-6">
          <h1 className="text-3xl font-semibold tracking-tight" data-testid="css-header-title">
            {headerTitle || "Crew Shape Search"}
          </h1>
          <p className="text-sm text-[var(--avcp-ink-soft)] mt-1">
            {headerDescription ||
              "Describe the crew you need. We match companies and independent contractors whose workforce offering can fill every row."}
          </p>
          {marketplace && (
            <p className="text-xs text-[var(--avcp-ink-soft)] mt-2"
               data-testid="css-marketplace-pill">
              Searching inside the <strong>{marketplace.replace("_", " ")}</strong> marketplace.
              Only entities listed in this marketplace with status <code>active</code> are shown.
            </p>
          )}
        </header>

        {/* Crew shape builder */}
        <section className="avcp-card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-3">What crew do you need?</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
                  <th className="py-2 pr-3">Resource type</th>
                  <th className="py-2 pr-3 w-20">Qty</th>
                  <th className="py-2 pr-3 w-44">Requires site lead?</th>
                  <th className="py-2 pr-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {requirements.map((r, idx) => (
                  <tr key={idx}
                      className="border-b border-[var(--avcp-line)] align-top"
                      data-testid={`${TID.cssReqRow}-${idx}`}>
                    <td className="py-2 pr-3">
                      <select
                        className="avcp-input"
                        data-testid={`${TID.cssReqTitle}-${idx}`}
                        value={r.title_code}
                        onChange={(e) => updateReq(idx, { title_code: e.target.value })}
                      >
                        <option value="">— Select —</option>
                        {titles.map((t) => {
                          const disabled = t.code !== r.title_code && usedCodes.has(t.code);
                          return (
                            <option key={t.code} value={t.code} disabled={disabled}>
                              {t.display_label_default}
                              {disabled ? " (used above)" : ""}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min="1"
                        max="999"
                        className="avcp-input"
                        data-testid={`${TID.cssReqQty}-${idx}`}
                        value={r.quantity}
                        onChange={(e) => updateReq(idx, { quantity: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <label className="inline-flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          data-testid={`${TID.cssReqSiteLead}-${idx}`}
                          checked={!!r.site_lead_capable}
                          onChange={(e) =>
                            updateReq(idx, { site_lead_capable: e.target.checked })
                          }
                        />
                        <span title="Only match rows that can run the job on site without a client-supplied lead">
                          Site lead capable
                        </span>
                      </label>
                    </td>
                    <td className="py-2 pr-3">
                      {requirements.length > 1 && (
                        <button
                          type="button"
                          className="text-[var(--avcp-ink-soft)] hover:text-red-600"
                          data-testid={`${TID.cssReqRemove}-${idx}`}
                          title="Remove row"
                          onClick={() => removeReq(idx)}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="avcp-btn mt-3"
                  data-testid={TID.cssReqAdd} onClick={addReq}>
            + Add another resource type
          </button>
        </section>

        {/* Filters */}
        <section className="avcp-card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-3">Refine</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium block mb-1">Capability track</span>
              <select
                className="avcp-input"
                data-testid={TID.cssTrack}
                value={trackSlug}
                onChange={(e) => setTrackSlug(e.target.value)}
              >
                <option value="">Any track</option>
                {tracks.map((t) => (
                  <option key={t.slug || t.code} value={t.slug || t.code}>
                    {t.display_name || t.label || t.slug}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium block mb-1">Service region</span>
              <select
                className="avcp-input"
                data-testid={TID.cssRegion}
                value={regionCode}
                onChange={(e) => setRegionCode(e.target.value)}
              >
                <option value="">Any region</option>
                {regions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label || r.code}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={TID.cssVerifiedOnly}
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
              />
              <span>Verified capability only</span>
              <span className="text-xs text-[var(--avcp-ink-soft)]">
                (require Phase-12 verified role evidence)
              </span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={TID.cssIncludeCompanies}
                checked={includeCompanies}
                onChange={(e) => setIncludeCompanies(e.target.checked)}
              />
              <span>Include companies</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={TID.cssIncludeSolo}
                checked={includeSolo}
                onChange={(e) => setIncludeSolo(e.target.checked)}
              />
              <span>Include independent contractors</span>
            </label>
          </div>

          {/* Keyword — intentionally de-emphasized */}
          <details className="mt-4">
            <summary className="text-xs text-[var(--avcp-ink-soft)] cursor-pointer select-none">
              Optional keyword (secondary; search is capability-first)
            </summary>
            <input
              type="text"
              className="avcp-input mt-2 max-w-md"
              placeholder="e.g. ACME Integration"
              data-testid={TID.cssKeyword}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </details>

          <div className="mt-5">
            <button
              type="button"
              className="avcp-btn primary"
              data-testid={TID.cssSubmit}
              disabled={busy}
              onClick={submit}
            >
              {busy ? "Searching…" : "Match crew shape"}
            </button>
          </div>
        </section>

        {/* Results */}
        {searched && (
          <section className="space-y-4">
            <header className="flex items-baseline justify-between">
              <h2 className="text-lg font-semibold">
                {results.length === 0
                  ? "No matches yet"
                  : `${results.length} match${results.length === 1 ? "" : "es"}`}
              </h2>
              {results.length > 0 && (
                <span className="text-xs text-[var(--avcp-ink-soft)]">
                  Sorted by full coverage, then match score, then total hourly cost
                </span>
              )}
            </header>

            {results.length === 0 && (
              <div className="avcp-card p-6 text-sm text-[var(--avcp-ink-soft)]"
                   data-testid={TID.cssResultsEmpty}>
                No company or contractor satisfies any row of the crew shape yet.
                Try relaxing the filters, removing the Verified-only toggle, or
                broadening the qty.
              </div>
            )}

            {results.map((card, idx) => (
              <ResultCard
                key={`${card.owner_kind}-${card.owner_id}`}
                card={card}
                idx={idx}
                marketplace={marketplace}
                onSendRFQ={() => {
                  if (!marketplace) {
                    toast.error(
                      "Open this search from a specific marketplace (Integrator / Workforce / Event Tech) to send an RFQ."
                    );
                    return;
                  }
                  setRfqTarget(card);
                  setRfqSnap({
                    requirements: requirements
                      .filter((r) => r.title_code && Number(r.quantity) > 0)
                      .map((r) => ({ title_code: r.title_code, quantity: Number(r.quantity) || 1 })),
                    track_slug: trackSlug || null,
                    region_code: regionCode || null,
                  });
                }}
              />
            ))}
          </section>
        )}

        {rfqTarget && rfqSnap && (
          <SendRFQModal
            open={!!rfqTarget}
            card={rfqTarget}
            marketplace={marketplace}
            requirements={rfqSnap.requirements}
            trackSlug={rfqSnap.track_slug}
            regionCode={rfqSnap.region_code}
            onClose={() => {
              setRfqTarget(null);
              setRfqSnap(null);
            }}
          />
        )}
      </div>
    </Layout>
  );
}

function ResultCard({ card, idx, marketplace, onSendRFQ }) {
  const ev = EVIDENCE_PILL[card.evidence_level] || EVIDENCE_PILL.self;
  return (
    <article
      className="avcp-card p-5"
      data-testid={`${TID.cssResultCard}-${idx}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-lg font-semibold">
            {card.display_name}
            {card.legal_name && card.legal_name !== card.display_name && (
              <span className="text-sm font-normal text-[var(--avcp-ink-soft)] ml-2">
                {card.legal_name}
              </span>
            )}
          </h3>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Pill tone={card.owner_kind === "org" ? "company" : "self"}>
              {card.owner_kind === "org" ? "Company" : "Independent contractor"}
            </Pill>
            {card.employee_count_band && (
              <Pill tone="neutral">{card.employee_count_band}</Pill>
            )}
            <Pill tone={ev.tone} testid={`${TID.cssResultEvidence}-${idx}`}>
              {ev.label}
            </Pill>
            {card.fully_covers ? (
              <Pill tone="ok">Fully covers crew</Pill>
            ) : (
              <Pill tone="warn">
                Partial · {card.matched_count}/{card.total_requirements}
              </Pill>
            )}
            {card.track_match && card.primary_track_codes?.length > 0 && (
              <Pill tone="info">
                Track: {card.primary_track_codes.join(", ")}
              </Pill>
            )}
            {card.insurance_status && card.insurance_status !== "none" && (
              <Pill tone="ok">Insurance: {card.insurance_status}</Pill>
            )}
          </div>
          {card.coverage_area_summary && (
            <p className="text-xs text-[var(--avcp-ink-soft)] mt-2">
              Coverage: {card.coverage_area_summary}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs text-[var(--avcp-ink-soft)]">Crew hourly total</div>
          <div className="text-xl font-semibold tabular-nums">
            {card.estimated_hourly_total != null
              ? `$${card.estimated_hourly_total.toFixed(2)}/hr`
              : "—"}
          </div>
          <div className="text-xs text-[var(--avcp-ink-soft)]">
            Match score {Math.round((card.match_score || 0) * 100)}%
          </div>
          {marketplace && (
            <button
              type="button"
              className="avcp-btn primary mt-3"
              data-testid={`${TID.cssResultSendRfq}-${idx}`}
              onClick={onSendRFQ}
              title="Send a Request for Quote — opens a communication thread, no money moves."
            >
              Send RFQ
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid={`${TID.cssResultMatched}-${idx}`}>
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
              <th className="py-2 pr-3">Requirement</th>
              <th className="py-2 pr-3">Resource type</th>
              <th className="py-2 pr-3">Need</th>
              <th className="py-2 pr-3">Available</th>
              <th className="py-2 pr-3">Hourly rate</th>
              <th className="py-2 pr-3">Site lead</th>
            </tr>
          </thead>
          <tbody>
            {card.matched_rows.map((r) => (
              <tr key={r.requirement_index} className="border-b border-[var(--avcp-line)]">
                <td className="py-2 pr-3 text-[var(--avcp-ink-soft)]">#{r.requirement_index + 1}</td>
                <td className="py-2 pr-3 font-medium">{r.title_label}</td>
                <td className="py-2 pr-3 tabular-nums">{r.quantity_required}</td>
                <td className="py-2 pr-3 tabular-nums">{r.quantity_available}</td>
                <td className="py-2 pr-3 tabular-nums">
                  {r.currency} {Number(r.hourly_rate).toFixed(2)}
                </td>
                <td className="py-2 pr-3">
                  {r.site_lead_capable ? (
                    <Pill tone="ok">site lead</Pill>
                  ) : r.site_lead_required ? (
                    <Pill tone="danger">required, none</Pill>
                  ) : (
                    <span className="text-xs text-[var(--avcp-ink-soft)]">—</span>
                  )}
                </td>
              </tr>
            ))}
            {card.unmatched_requirements?.length > 0 &&
              card.unmatched_requirements.map((r) => (
                <tr key={`u-${r.requirement_index}`}
                    className="border-b border-[var(--avcp-line)] bg-rose-50/40"
                    data-testid={`${TID.cssResultUnmatched}-${idx}-${r.requirement_index}`}>
                  <td className="py-2 pr-3 text-[var(--avcp-ink-soft)]">#{r.requirement_index + 1}</td>
                  <td className="py-2 pr-3 font-medium">{r.title_code}</td>
                  <td className="py-2 pr-3 tabular-nums">{r.quantity_required}</td>
                  <td className="py-2 pr-3 tabular-nums text-rose-600">no match</td>
                  <td className="py-2 pr-3">—</td>
                  <td className="py-2 pr-3">
                    {r.site_lead_required && <Pill tone="danger">site lead needed</Pill>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}
